import { isCanceledSubscriptionStatus, isSubInTrial, listActiveSubscriptions } from './subscriptionStatuses.js';
import { PRODUCTS, ADDON_PRODUCTS } from './config.js';
import { computeTrialCapable, computeUseTrial, priceMeta } from './priceCatalog.js';
import { findActiveAnalyzer, findActiveMcp, productFromSubscription, subscriptionBillingCycle } from './selectionFromSubscription.js';
import {
  dedicatedEnvLinesOf,
  findLineItemForProduct,
  findLineItemsForProduct,
  isDedicatedEnv,
  isDedicatedProduct,
  subscriptionIncludesProduct,
  subscriptionLineItems
} from './subscriptionLines.js';

/**
 * Product priority for picking a cycle's primary (anchor) subscription.
 * Higher wins. See DECISION_TREE.md §7.
 */
export const PRODUCT_PRIORITY = {
  [PRODUCTS.standard]: 5,
  [PRODUCTS.premium]: 4,
  [ADDON_PRODUCTS.dedicated]: 3,
  [ADDON_PRODUCTS.dedicatedFutures]: 3,
  [ADDON_PRODUCTS.analyzer]: 2,
  [PRODUCTS.futures]: 1
};

export function subPriority(sub, catalog) {
  let max = 0;
  for (const line of subscriptionLineItems(sub)) {
    const meta = priceMeta(catalog?.getById?.(line.priceId));
    const p = PRODUCT_PRIORITY[meta?.product] || 0;
    if (p > max) max = p;
  }
  if (max === 0) {
    const p = PRODUCT_PRIORITY[productFromSubscription(sub)] || 0;
    if (p > max) max = p;
  }
  return max;
}

/**
 * Pick the anchor (primary) sub for a cycle: highest priority, older `created` wins on tie.
 * DE subs are never primary (they own their own subscription).
 */
export function primarySubForCycle(subscriptions, cycle, catalog) {
  const onCycle = listActiveSubscriptions(subscriptions).filter((s) => !isDedicatedEnv(s) && subscriptionBillingCycle(s) === cycle);
  if (!onCycle.length) return null;
  onCycle.sort((a, b) => {
    const pb = subPriority(b, catalog) - subPriority(a, catalog);
    if (pb !== 0) return pb;
    return Number(a.created || a.Created || 0) - Number(b.created || b.Created || 0);
  });
  return onCycle[0];
}

/**
 * Pick host sub for a new DE line item on a cycle.
 * Priority: legacy DE-only sub → any sub already carrying a DE line → cycle primary anchor.
 * Returns null when no sub exists on the cycle (caller emits CreateSub).
 */
export function hostSubForDE(subscriptions, cycle, catalog) {
  const active = listActiveSubscriptions(subscriptions);
  const onCycle = active.filter((s) => subscriptionBillingCycle(s) === cycle);
  if (!onCycle.length) return null;
  const legacy = onCycle.find((s) => isDedicatedEnv(s));
  if (legacy) return legacy;
  const carrier = onCycle.find((s) => dedicatedEnvLinesOf(s, catalog).length > 0);
  if (carrier) return carrier;
  return primarySubForCycle(active, cycle, catalog);
}

/** Sub on a cycle that contains the given product, with its line item. */
export function ownerSubForProductOnCycle(subscriptions, product, cycle, catalog) {
  for (const sub of listActiveSubscriptions(subscriptions)) {
    if (isDedicatedEnv(sub)) continue;
    if (subscriptionBillingCycle(sub) !== cycle) continue;
    const line = findLineItemForProduct(sub, product, catalog);
    if (line) return { sub, line };
  }
  return null;
}

/** Group desired items by cycle, attaching product+cycle metadata per item. */
export function groupItemsByCycle(items, catalog) {
  const groups = new Map();
  for (const it of items) {
    const qty = Number(it.quantity) || 0;
    if (qty <= 0) continue;
    const meta = priceMeta(catalog?.getById?.(it.price_id));
    if (!meta) continue;
    if (!groups.has(meta.cycle)) groups.set(meta.cycle, []);
    groups.get(meta.cycle).push({
      price_id: it.price_id,
      quantity: qty,
      product: meta.product,
      cycle: meta.cycle
    });
  }
  return groups;
}

export function pickActiveSubscription(subscriptions = []) {
  return listActiveSubscriptions(subscriptions)[0] ?? null;
}

export function hasActiveSubscription(subscriptions = []) {
  return listActiveSubscriptions(subscriptions).length > 0;
}

/** First active subscription per product line (legacy users may have one sub per product). */
export function primarySubByProduct(activeSubs) {
  const map = new Map();
  for (const sub of activeSubs) {
    const product = productFromSubscription(sub);
    if (!product || map.has(product)) continue;
    map.set(product, sub);
  }
  return map;
}

/** @typedef {{ price_id: string, quantity: number }} CheckoutItem */

/** Product line for a catalog price id. */
export function productForPriceId(priceId, catalog) {
  const price = catalog?.getById?.(priceId);
  return priceMeta(price)?.product ?? null;
}

/** Whether the account already has an active/trialing sub for this product line. */
export function hasActiveSubscriptionForProduct(subscriptions, product, catalog) {
  return subscriptionIncludesProduct(listActiveSubscriptions(subscriptions), product, catalog);
}

/**
 * @param {object[]} subscriptions
 * @returns {{ activeSubs: object[], byProduct: Map<string, object>, hasMultiple: boolean, duplicateProducts: string[] }}
 */
export function summarizeSubscriptions(subscriptions = []) {
  const activeSubs = listActiveSubscriptions(subscriptions);
  const byProduct = primarySubByProduct(activeSubs);
  const productCounts = new Map();
  for (const sub of activeSubs) {
    const product = productFromSubscription(sub);
    if (!product) continue;
    productCounts.set(product, (productCounts.get(product) || 0) + 1);
  }
  const duplicateProducts = [...productCounts.entries()].filter(([, n]) => n > 1).map(([p]) => p);

  return {
    activeSubs,
    byProduct,
    hasMultiple: activeSubs.length > 1,
    duplicateProducts
  };
}

/**
 * Decide upcoming-invoice preview type.
 * @returns {"Create" | "Update"}
 */
export function invoiceTypeForCheckout(subscriptions) {
  return hasActiveSubscription(subscriptions) ? 'Update' : 'Create';
}

/** Active sub first; otherwise a canceled sub (reactivate). */
export function pickManageableSubscription(subscriptions = []) {
  const active = pickActiveSubscription(subscriptions);
  if (active) return active;
  return subscriptions.find((s) => isCanceledSubscriptionStatus(s.status ?? s.Status)) || null;
}

/**
 * Body for POST /api/billing/upcoming-invoice (existing subscribers).
 * Preview reflects prorated seat change on one subscription line (Stripe upcoming invoice).
 * @deprecated use buildPreviewCheckoutBody (multi-item) instead.
 * @returns {object | null}
 */
export function buildUpcomingInvoicePreviewBody({ activeSubscription, totalSeats, selection, catalog }) {
  const sub = activeSubscription;
  if (!sub?.id) return null;

  let targetProduct = null;
  let seats = 0;
  if (selection) {
    if ((selection.standardCount || 0) > 0) {
      targetProduct = PRODUCTS.standard;
      seats = selection.standardCount;
    } else if ((selection.premiumCount || 0) > 0) {
      targetProduct = PRODUCTS.premium;
      seats = selection.premiumCount;
    } else if ((selection.futuresCount || 0) > 0) {
      targetProduct = PRODUCTS.futures;
      seats = selection.futuresCount;
    }
  }
  if (seats < 1 && Number(totalSeats) > 0) {
    seats = Number(totalSeats);
  }

  const line = targetProduct && catalog ? findLineItemForProduct(sub, targetProduct, catalog) : null;
  const currentQty = line ? Number(line.quantity) || 0 : Number(sub.quantity) || 0;

  if (seats < 1) {
    seats = currentQty;
  }
  if (seats < 1) return null;
  if (seats === currentQty) {
    return null;
  }

  return {
    invoice_type: 'Update',
    subscription_id: sub.id,
    quantity: seats,
    price_id: line?.priceId ?? '',
    coupon: ''
  };
}

/**
 * Seat-change routing when a product carries TWO lines on one sub (legacy +
 * new price); the UI quantity is their sum.
 *   • Increase → onto the new line; legacy frozen. No new line: grow legacy and
 *     let BE superseded-migration move it (priceId left unset).
 *   • Decrease → shed the most expensive seats first so the cheapest/free line
 *     survives; ties keep the new-price line. Relies on per-line `unitAmount`
 *     (a seat freed via a sub-level discount, not a $0 price, reads as paid).
 * @returns {{ modifies: object[], removes: object[] }}
 */
export function planMultiLineSeatChange(sub, lines, targetTotal, desiredCatalogPriceId, catalog) {
  const subId = sub?.id ?? sub?.Id ?? null;
  const desiredPrice = catalog?.getById?.(desiredCatalogPriceId);
  const desiredStripeId = desiredPrice?.stripePriceId || desiredPrice?.stripe_price_id || '';

  const work = lines
    .filter((l) => (l.subscriptionItemId ?? '') !== '')
    .map((l) => ({
      subItemId: l.subscriptionItemId,
      origQty: Math.max(0, Number(l.quantity) || 0),
      qty: Math.max(0, Number(l.quantity) || 0),
      unit: Math.max(0, Number(l.unitAmount ?? l.unit_amount) || 0),
      isNew: Boolean(desiredStripeId && l.priceId === desiredStripeId),
      cancel: false
    }));

  const result = { modifies: [], removes: [] };
  if (!subId || work.length === 0) return result;

  const newLine = work.find((l) => l.isNew) || null;
  const legacyLines = work.filter((l) => l !== newLine);
  const currentTotal = work.reduce((s, l) => s + l.qty, 0);

  if (targetTotal === currentTotal) return result; // no-op

  if (targetTotal > currentTotal) {
    // Grow the new line, or the largest legacy line if there is none.
    const delta = targetTotal - currentTotal;
    const grow = newLine || legacyLines.reduce((a, b) => (b.qty > a.qty ? b : a), legacyLines[0]);
    grow.qty += delta;
  } else {
    let delta = currentTotal - targetTotal;
    // Most expensive first; ties shed legacy before new.
    const shed = work.slice().sort((a, b) => b.unit - a.unit || Number(a.isNew) - Number(b.isNew));
    for (const l of shed) {
      if (delta <= 0) break;
      const take = Math.min(delta, Math.max(0, l.qty - 1));
      l.qty -= take;
      delta -= take;
    }
    // Cancel from the front, never the last (cheapest) survivor.
    for (let i = 0; i < shed.length - 1 && delta > 0; i++) {
      shed[i].cancel = true;
      delta -= shed[i].qty;
      shed[i].qty = 0;
    }
    // Remaining delta only if target < 1 seat (UI clamps) — leave survivor at 1.
  }

  for (const l of work) {
    if (l.cancel) {
      result.removes.push({ subscriptionId: subId, subscriptionItemId: l.subItemId });
    } else if (l.qty !== l.origQty) {
      result.modifies.push({ subscriptionId: subId, subscriptionItemId: l.subItemId, quantity: l.qty });
    }
  }
  return result;
}

/**
 * Cycle-keyed, per-item router. See DECISION_TREE.md §3 + PLAN.md Phase 5.
 *
 * Per desired item (price_id + quantity), routes to exactly one outcome:
 *
 *   1. DE product
 *      → one CreateSub per env unit (qty expanded; each isolated, isDedi:true).
 *        DE always billed as its own subscription — never bundled, never
 *        modified-in-place.
 *
 *   2. Owner sub found for product on cycle (ownerSubForProductOnCycle)
 *      2a. qty equals current line qty → skip (no-op for this item).
 *      2b. qty differs → Modify on that sub's line item (prorated by BE).
 *
 *   3. No owner + primary anchor exists on cycle (primarySubForCycle)
 *      → AddItem onto the primary anchor sub (prorated by BE).
 *
 *   4. No owner + no sub on cycle
 *      → CreateSub seeding the cycle. Multiple new items in one cart group
 *        into a single CreateSub call (bundle).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Why AddItem to the anchor instead of CreateSub-per-new-product:
 *
 * Legacy accounts often have multiple single-line subs on the same cycle
 * (one sub per product). When the user adds a brand-new product to such
 * an account, two strategies are possible:
 *
 *   (A) AddItem to the priority-winning anchor sub  ← current rule
 *   (B) CreateSub a fresh sub for the new product   ← rejected
 *
 * (A) is preferred because:
 *
 *   • Convergent: the anchor sub gradually absorbs new products, healing
 *     fragmentation over time. (B) freezes the fragmented shape forever
 *     and keeps growing the anchor count.
 *   • Proration matches mental model: "added mid-cycle, charged for
 *     remaining days on my existing bill" — BE uses AlwaysInvoice, so
 *     the user gets one prorated charge against the anchor's period.
 *     (B) would full-charge a new cycle and add a new renewal anchor.
 *   • Fewer Stripe subscriptions / fewer renewal invoices / less dunning
 *     surface area / less webhook noise.
 *   • Same or fewer gRPC calls than (B) for the same cart.
 *
 * Anchor selection: highest PRODUCT_PRIORITY wins; older `created` breaks
 * ties; DE subs excluded. Side effect: the anchor sub grows into a
 * multi-line bundle; non-anchor legacy subs remain solo until the user
 * explicitly cancels their line and re-adds. Full consolidation is not
 * automatic by design — router never cancels existing subs.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * Returns `{type:"multi-cycle", ops:[{cycle, subId, modifies, adds, creates, isDedi?}]}`
 * or `{type:"noop"}` when every desired item matched current state exactly.
 */
export function resolveCheckoutUpdates(desiredItems, subscriptions, catalog, trialContext = null) {
  const active = listActiveSubscriptions(subscriptions);
  // Per-product one-time trial state + the buyer's trial-or-buy choice, used to
  // decide whether a new product must spawn its own (trial-bearing) sub instead
  // of being absorbed as a line item on an existing sub.
  const usedProductIds = trialContext?.usedProductIds || [];
  const skipTrial = Boolean(trialContext?.skipTrial);

  // Track existing DE lines per cycle as ordered refs so DE desired items can
  // be matched 1:1 against existing lines. Same order as selection.dedicated
  // (both come from selectionForCycle / dedicatedEnvArrays) so qty modifies
  // land on the right env. Pop on consume.
  /** @type {Map<string, Array<{subId: string, subItemId: string|null, currentQty: number}>>} */
  const deLinesByCycle = new Map();
  for (const sub of active) {
    const deLines = dedicatedEnvLinesOf(sub, catalog);
    if (deLines.length === 0) continue;
    const cycle = subscriptionBillingCycle(sub);
    if (!deLinesByCycle.has(cycle)) deLinesByCycle.set(cycle, []);
    const refs = deLinesByCycle.get(cycle);
    for (const line of deLines) {
      refs.push({
        subId: sub.id ?? sub.Id ?? null,
        subItemId: line.subscriptionItemId ?? null,
        currentQty: Math.max(0, Number(line.quantity) || 0)
      });
    }
  }

  /** @type {Array<{cycle:string, subId:string|null, modifies:object[], adds:object[], creates:object[], isDedi?:boolean}>} */
  const ops = [];
  /** Per-cycle accumulators for non-DE items, keyed by cycle string. */
  const perCycle = new Map();

  for (const it of desiredItems) {
    const qty = Number(it.quantity) || 0;
    if (qty <= 0) continue;
    const meta = priceMeta(catalog?.getById?.(it.price_id));
    if (!meta) continue;

    // Analyzer is globally unique per user — one purchase covers all
    // accounts on any cycle. Drop attempts to add/create analyzer on a
    // different cycle than where it's already owned. FE blocks the UI;
    // this is a defensive filter for malformed carts.
    if (meta.product === ADDON_PRODUCTS.analyzer) {
      const owned = findActiveAnalyzer(active, catalog);
      if (owned && owned.cycle !== meta.cycle) continue;
    }

    // MCP (AI Trading) is globally unique per user like analyzer — one
    // subscription covers any cycle. Drop attempts to add/create MCP on a
    // different cycle than where it's already owned (a cross-cycle request is
    // a cadence change, not a second line). FE blocks the UI; defensive filter
    // for malformed carts.
    if (meta.product === ADDON_PRODUCTS.mcp) {
      const owned = findActiveMcp(active, catalog);
      if (owned && owned.cycle !== meta.cycle) continue;
    }

    // DE: one env per desired item. Skip items already covered by an existing
    // line (delta-only ADD). For real adds, route to a DE-aware host:
    // legacy DE-only sub → carrier sub → cycle anchor. Routing to anchor when
    // a same-cycle DE host exists triggers Stripe duplicate-price errors.
    if (isDedicatedProduct(meta.product)) {
      if (!perCycle.has(meta.cycle)) {
        perCycle.set(meta.cycle, { modifies: [], adds: [], creates: [], removes: [] });
      }
      const bucket = perCycle.get(meta.cycle);
      const existing = deLinesByCycle.get(meta.cycle) || [];
      const existingLine = existing.shift();
      if (existingLine && existingLine.subId && existingLine.subItemId) {
        if (qty !== existingLine.currentQty) {
          bucket.modifies.push({
            subscriptionId: existingLine.subId,
            quantity: qty,
            subscriptionItemId: existingLine.subItemId
          });
        }
        continue;
      }
      if (existingLine) continue; // existing DE without item id — opaque, skip safely
      // Each additional DE env must use its OWN subscription. Stripe rejects
      // a sub containing two items with the same price_id, and every env
      // uses the same DE price — so we can't bundle a second env into the
      // host sub via AddSubscriptionItem. Always emit a fresh CreateSub for
      // envs beyond what's already represented by existing DE lines.
      bucket.creates.push({ price_id: it.price_id, quantity: qty });
      continue;
    }

    if (!perCycle.has(meta.cycle)) {
      perCycle.set(meta.cycle, {
        modifies: [],
        adds: [],
        creates: [],
        removes: []
      });
    }
    const bucket = perCycle.get(meta.cycle);

    const owner = ownerSubForProductOnCycle(active, meta.product, meta.cycle, catalog);
    if (owner) {
      // Two lines for one product (legacy + new price): route via the multi-line
      // planner instead of treating the first line as the whole product.
      const productLines = findLineItemsForProduct(owner.sub, meta.product, catalog);
      if (productLines.length > 1) {
        const { modifies, removes } = planMultiLineSeatChange(owner.sub, productLines, qty, it.price_id, catalog);
        for (const m of modifies) bucket.modifies.push(m);
        for (const r of removes) bucket.removes.push(r);
        continue;
      }

      const currentQty = Math.max(0, Number(owner.line.quantity) || 0);
      const currentPriceId = owner.line.priceId || '';
      // Variant swap (e.g. futures pay-as-you-go → futures_unlimited): same
      // product+cycle but different SKU. Compare VARIANTS, not raw priceIds —
      // Stripe price IDs can rotate (price archive/replace) for the same
      // logical SKU, and a bare priceId !== priceId check would emit a bogus
      // swap modify on every render, polluting preview with proration churn
      // for items the user never touched.
      // Both meta lookups must succeed AND yield non-empty variants for a
      // valid swap comparison. If the existing priceId isn't in the catalog
      // (legacy/archived) OR either variant is unknown, assume same variant —
      // bias toward NO swap so existing items don't get re-charged on preview.
      // Catalog `byId` is keyed by BOTH internal UUID and Stripe price ID, so
      // owner.line.priceId (Stripe `price_xxx`) resolves to the catalog entry
      // directly. Compare variants to detect a real SKU swap (e.g. futures
      // default → unlimited) vs a same-SKU stale priceId mismatch.
      const currentMeta = priceMeta(catalog?.getById?.(currentPriceId));
      const isPriceSwap = Boolean(
        currentPriceId && currentPriceId !== it.price_id && currentMeta?.variant && meta?.variant && currentMeta.variant !== meta.variant
      );
      if (qty !== currentQty || isPriceSwap) {
        // BM/Stripe SubscriptionItemDelta.price_id expects a STRIPE price ID
        // (price_xxx). `it.price_id` is the internal catalog UUID — resolve it
        // through the catalog to get the Stripe ID before serializing.
        let swapStripePriceId = '';
        if (isPriceSwap) {
          const desiredPrice = catalog?.getById?.(it.price_id);
          swapStripePriceId = desiredPrice?.stripePriceId || desiredPrice?.stripe_price_id || '';
        }
        bucket.modifies.push({
          subscriptionId: owner.sub.id ?? owner.sub.Id,
          quantity: qty,
          subscriptionItemId: owner.line.subscriptionItemId ?? '',
          // Only set when actually swapping — empty string means "keep current price".
          priceId: swapStripePriceId
        });
      }
      continue;
    }

    const primary = primarySubForCycle(active, meta.cycle, catalog);
    // Does a trial genuinely apply to THIS new product? Per-product eligibility:
    // trial-enabled tiered price, qty within the included bundle, one-time trial
    // not yet consumed, and the buyer didn't pick "Buy now". Mirrors the BM
    // CreateSubscription gate so the FE routes exactly what BM will grant.
    const trialApplies =
      !skipTrial && computeUseTrial([{ price_id: it.price_id, quantity: qty }], catalog, usedProductIds);

    // When a trial applies, the product MUST get its own subscription: a
    // line-item add onto an existing (non-trialing) sub can never carry a trial
    // — Stripe scopes trial_end to the whole sub and AddSubscriptionItem has no
    // trial path, so the trial is silently dropped. Spawning a separate sub
    // routes it through createSubscriptionWithTrial and avails the trial.
    //
    // When NO trial applies (already used, over the included bundle, or "Buy
    // now"), fall back to the normal anchor logic: absorb into the existing
    // non-trialing primary as a line item, else create. This also covers the
    // pre-existing footgun of adding to a sub that's itself still trialing —
    // that still forks to creates via the final branch. Owner-path bumps (qty
    // change on a product already on the sub) are handled above and unaffected.
    if (trialApplies) {
      bucket.creates.push({ price_id: it.price_id, quantity: qty });
    } else if (primary && !isSubInTrial(primary)) {
      bucket.adds.push({ price_id: it.price_id, quantity: qty });
    } else {
      bucket.creates.push({ price_id: it.price_id, quantity: qty });
    }
  }

  for (const [cycle, bucket] of perCycle) {
    if (!bucket.modifies.length && !bucket.adds.length && !bucket.creates.length && !bucket.removes.length) continue;
    const primary = primarySubForCycle(active, cycle, catalog);
    ops.push({
      cycle,
      subId: primary?.id ?? primary?.Id ?? null,
      modifies: bucket.modifies,
      adds: bucket.adds,
      creates: bucket.creates,
      removes: bucket.removes
    });
  }

  if (!ops.length) return { type: 'noop' };
  return { type: 'multi-cycle', ops };
}

/**
 * Whether a resolved plan has any actionable change — including removes (a
 * collapse is a removes-only op that would otherwise read as no-op).
 */
export function planHasChanges(plan) {
  if (!plan || plan.type !== 'multi-cycle') return false;
  return plan.ops.some(
    (op) =>
      (op.modifies?.length || 0) > 0 ||
      (op.adds?.length || 0) > 0 ||
      (op.creates?.length || 0) > 0 ||
      (op.removes?.length || 0) > 0
  );
}

/**
 * Split a `creates` bucket into the items that will form a TRIAL subscription
 * and the items that must be a PAID subscription. A trial is per-SUBSCRIPTION
 * (Stripe scopes trial_end to the whole sub; BM rejects a sub unless every item
 * is trial-eligible), so a trial-eligible product (Futures) must NEVER share a
 * sub with a paid one — otherwise the paid item rides the trial free (revenue
 * loss) or BM rejects the whole sub. The executor creates one sub per group.
 *
 *   • trialItems — items individually trial-eligible that ALSO collectively form
 *     a valid trial sub (same trial days, etc.). Empty when skipTrial/DE.
 *   • paidItems  — everything else (created at full price, use_trial=false).
 *
 * Returns trialItems only when they're collectively valid; otherwise they fall
 * back into paidItems (fail safe: never trial something we're unsure about).
 */
export function splitCreatesForTrial(creates, catalog, usedProductIds = [], skipTrial = false, isDedi = false) {
  const all = creates || [];
  if (!all.length || skipTrial || isDedi) {
    return { trialItems: [], paidItems: all };
  }
  const trialCandidates = all.filter((it) => computeUseTrial([it], catalog, usedProductIds));
  const valid = trialCandidates.length > 0 && computeUseTrial(trialCandidates, catalog, usedProductIds);
  if (!valid) {
    return { trialItems: [], paidItems: all };
  }
  const trialSet = new Set(trialCandidates);
  return {
    trialItems: trialCandidates,
    paidItems: all.filter((it) => !trialSet.has(it)),
  };
}

/**
 * Trial summary for a resolved plan, scoped to its new-subscription (`creates`)
 * items — NOT the whole cart. Because the executor splits each create bucket so
 * a trial product gets its OWN sub, eligibility is judged PER ITEM here: a
 * single trial-eligible item makes the cart trial-capable even when it shares a
 * bucket with paid products. Drives the trial choice UI, preview total and
 * $0-due messaging so all three agree with what the executor will create.
 *
 *   • capable  — some create item is a trial-enabled tiered product (qty-
 *     independent) → show the choice (greyed if over the included bundle).
 *   • eligible — some create item is fully trial-eligible (qty within bundle,
 *     not already used) → the "Free trial" option is selectable.
 *   • applies  — eligible AND the buyer didn't pick "Buy now".
 *   • days / includedBase — from the first trial-enabled create price.
 *
 * Pass a plan resolved with skipTrial:false so trial products are routed to
 * `creates` and remain detectable even after the buyer picks "Buy now".
 */
/**
 * Catalog product keys ('futures', 'standard', …) whose CREATED subscription
 * will start on a free trial.
 *
 * `planTrialSummary().applies` only answers "does anything in this cart trial",
 * which is enough for a whole-cart quote and wrong for anything per line. A
 * cart of paid accounts plus a Futures trial has to show the accounts priced
 * and the Futures line at $0 today — otherwise the lines contradict the total,
 * and a discount code looks like it covers a line it can never reach (codes are
 * never attached to trial buckets, and nothing carries over when the trial
 * converts).
 *
 * Returns an empty set when the buyer chose "Buy now" (skipTrial).
 *
 * @param {object | null} plan
 * @param {object | null} catalog
 * @param {string[]} [usedProductIds]
 * @param {boolean} [skipTrial]
 * @returns {Set<string>}
 */
export function trialingProductKeys(plan, catalog, usedProductIds = [], skipTrial = false) {
  const keys = new Set();
  if (skipTrial || !plan || plan.type !== 'multi-cycle' || !catalog) return keys;

  for (const op of plan.ops) {
    if (op.isDedi) continue;
    for (const it of op.creates || []) {
      if (!computeUseTrial([it], catalog, usedProductIds)) continue;
      const product = priceMeta(catalog.getById?.(it.price_id))?.product;
      if (product) keys.add(product);
    }
  }
  return keys;
}

export function planTrialSummary(plan, catalog, usedProductIds = [], skipTrial = false) {
  const empty = { capable: false, eligible: false, applies: false, days: 0, includedBase: 0 };
  if (!plan || plan.type !== 'multi-cycle' || !catalog) return empty;

  let capable = false;
  let eligible = false;
  let days = 0;
  let includedBase = 0;

  for (const op of plan.ops) {
    if (op.isDedi) continue;
    for (const it of op.creates || []) {
      if (computeTrialCapable([it], catalog, usedProductIds)) {
        capable = true;
        if (days === 0) {
          const p = catalog.getById?.(it.price_id);
          const d = Number(p?.trial_period_days ?? 0);
          if (d > 0) {
            days = d;
            const up = Number(p?.tiers?.[0]?.up_to ?? p?.tiers?.[0]?.upTo ?? 0);
            if (up > 0) includedBase = up;
          }
        }
      }
      if (computeUseTrial([it], catalog, usedProductIds)) eligible = true;
    }
  }

  return { capable, eligible, applies: eligible && !skipTrial, days, includedBase };
}
